import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, CircularProgress, Backdrop, Box, Typography, TextField, LinearProgress } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { pdf } from "@react-pdf/renderer";
import EIRReportPdf from "./EIRReportPdf";
// import GeneralInfoReport from './GeneralInfoReport';
import CFRReport from "./CFRReport";
import CitationReport from "./CitationReport";
import ExhibitionReport from "./ExhibitionReport";
import styled from "@emotion/styled";
import inspectionApi from "../../../apis/inspectionApi";
import codeTablePostgresApi from "../../../apis/codeTablePostgresApi";
import lookupApi from "../../../apis/lookupApi";
import GeneralReport from "./GeneralReport";
// import AdministrativeReport from "./AdministrativeReport";
import EmployeeSignature from "./EmployeeSignature";
import { LOOKUP_CATEGORIES, COMMODITY_TENANT_LIST } from "../../../utilities/constants";
import DiscussionReport from "./DiscussionReport";
import ieqipCoreServiceApi from "../../../apis/ieqipCoreServiceApi";
import assignmentApi from "../../../apis/assignmentApi";
import PrintWatermark from "../PrintWatermark";
import assignmentAssignToApi from "../../../apis/assignmentAssignToApi";
import { getGlobalDate, getLocalDateTime, parseDateWithoutTimezone } from "../../../utilities/helpers";
import { SIGNATURE_REPORT_TYPES } from "../../../utilities/constants";

const ReportHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  borderBottom: '2px solid #222',
  paddingBottom: 8,
  marginBottom: 24,
}));

const HeaderLeft = styled(Box)({
  textAlign: 'left',
});
const HeaderRight = styled(Box)({
  textAlign: 'right',
  minWidth: 180,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.4rem',
  color: '#1a1a1a',
  marginTop: 32,
  marginBottom: 16,
  padding: '12px 20px',
  backgroundColor: '#f8f9fa',
  borderLeft: '6px solid #1976d2',
  borderRadius: '0 8px 8px 0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '4px',
    background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
    borderRadius: '0 8px 8px 0',
  }
}));

const SectionWrapper = styled(Box)(({ theme }) => ({
  marginBottom: 32,
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  '& .section-content': {
    padding: '20px',
  }
}));

const TOCRow = styled(Box)({
  display: 'flex',
  fontSize: '1rem',
  marginBottom: 2,
  paddingBottom: 4,
  borderBottom: '1px solid #e0e0e0',
});

const RootStyle = styled("div")({
  flexGrow: 1,
  margin: "20px",
});

const InspectionReport = () => {
  const reportComponentRef = useRef();
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isEIRGenerating, setIsEIRGenerating] = useState(false);
  const [watermarkText, setWatermarkText] = useState("");

  const handleGeneratePDF = async () => {
    // This function will be implemented in the InspectionReportPrintable component
    // where all the data is available
    if (reportComponentRef.current && reportComponentRef.current.generatePDF) {
      setIsPdfGenerating(true);
      try {
        const result = await reportComponentRef.current.generatePDF();
        if (result?.success) {
          // PDF was successfully generated and opened
          setTimeout(() => {
            setIsPdfGenerating(false);
          }, 2000);
        }
      } catch (error) {
        // Error handling is done in the child component
        console.error('PDF generation failed:', error);
        setIsPdfGenerating(false);
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateEIR = async () => {
    if (reportComponentRef.current?.getDataForEIR) {
      setIsEIRGenerating(true);
      try {
        const data = reportComponentRef.current.getDataForEIR();
        const blob = await pdf(<EIRReportPdf {...data} />).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `EIR-${data.assignment?.audited_firm_name || "report"}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (err) {
        console.error("EIR generation failed:", err);
        alert("Failed to generate EIR: " + err.message);
      } finally {
        setIsEIRGenerating(false);
      }
    }
  };

  return (
    <RootStyle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: "0.5em", flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
        >
          Print this out!
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleGenerateEIR}
          disabled={isEIRGenerating}
          startIcon={isEIRGenerating ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
        >
          {isEIRGenerating ? "Generating EIR..." : "Generate EIR"}
        </Button>
        <TextField
          label="Watermark text"
          placeholder="e.g. Privileged and Confidential"
          value={watermarkText}
          onChange={(e) => setWatermarkText(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <Typography variant="caption" sx={{ color: '#666' }}>Leave blank for no watermark</Typography>
        {/* Generate PDF button temporarily hidden */}
        {false && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleGeneratePDF}
            disabled={isPdfGenerating}
            startIcon={isPdfGenerating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isPdfGenerating ? 'Generating PDF...' : 'Generate PDF'}
          </Button>
        )}
      </Box>
      <InspectionReportPrintable ref={reportComponentRef} watermarkText={watermarkText} />
    </RootStyle>
  );
};

const PrintHeader = ({ assignment }) => (
  <Box className="print-header-only" sx={{ display: 'none', '@media print': { display: 'block' } }}>
    {/* Centered Title */}
    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: 1, textAlign: 'center' }}>
      Establishment Inspection Report
    </Typography>
    <Typography sx={{ fontSize: '0.9rem', marginBottom: 2, textAlign: 'center', fontStyle: 'italic' }}>
      Report written by Wynngate Corporation
    </Typography>
    
    <ReportHeader sx={{ marginBottom: '16px' }}>
      <HeaderLeft>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Auditing Firm:</b> {assignment?.auditing_firm_name}</Typography>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Address:</b> {assignment?.auditing_firm_address}</Typography>
      </HeaderLeft>
      <HeaderLeft>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Audited Firm:</b> {assignment?.audited_firm_name}</Typography>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Address:</b> {assignment?.audited_firm_address}</Typography>
      </HeaderLeft>
      <HeaderRight>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Client:</b> {assignment?.audited_client_firm_name}</Typography>
        <Typography sx={{ fontSize: '0.9rem' }}><b>Start:</b> {assignment?.inspection_dates?.length ? getLocalDateTime(Math.min(...assignment.inspection_dates.map(date => parseDateWithoutTimezone(date)))) : "-"}</Typography>
        <Typography sx={{ fontSize: '0.9rem' }}><b>End:</b> {assignment?.inspection_dates?.length ? getLocalDateTime(Math.max(...assignment.inspection_dates.map(date => parseDateWithoutTimezone(date)))) : "-"}</Typography>
      </HeaderRight>
    </ReportHeader>
  </Box>
);

const InspectionReportPrintable = React.forwardRef(({ watermarkText }, ref) => {
  const [inspection, setInspection] = useState(null);
  const [codeTables, setCodeTables] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lookupsByGeneral, setLookupsByGeneral] = useState(null);
  const [inspectionDetails, setInspectionDetails] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [leadUser, setLeadUser] = useState(null);
  const [inspectionExhibitions, setInspectionExhibitions] = useState(null);
  const [assignmentSignatures, setAssignmentSignatures] = useState(null);
  const [allowedAssignTos, setAllowedAssignTos] = useState(null);
  const [b503Operations, setB503Operations] = useState([]);
  const [a503Operations, setA503Operations] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsLoadedBytes, setDetailsLoadedBytes] = useState(0);
  const [detailsParsing, setDetailsParsing] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const tenantId = useSelector((state) => {
    return state.tenantId;
  });

  const idToken = useSelector((state) => {
    return state.token;
  });

  const loggedInUser = useSelector((state) => {
    return state.user;
  });

  const { inspectionUid } = useParams();

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    getDataForEIR: () => ({
      assignment,
      inspection,
      inspectionDetails,
      lookups,
      lookupsByGeneral,
      b503Operations,
      a503Operations,
      inspectionExhibitions,
      allowedAssignTos,
    }),
    generatePDF: async () => {
      if (!inspection || !assignment || !codeTables || !lookups || !lookupsByGeneral || !inspectionDetails || !assignmentSignatures || !allowedAssignTos) {
        alert('Data is not fully loaded yet. Please wait and try again.');
        return;
      }

      try {
        setLoading(true);
        setIsGeneratingPdf(true);

        // Prepare data for the API call
        const requestData = {
          assignment: assignment,
          inspection: inspection,
          code_tables: codeTables,
          lookups: lookups,
          lookupsByGeneral: lookupsByGeneral,
          inspection_details: inspectionDetails,
          inspection_exhibitions: inspectionExhibitions,
          assignment_signatures: assignmentSignatures,
          allowed_assign_tos: allowedAssignTos
        };

        // Make API call to generate PDF
        const response = await ieqipCoreServiceApi.post(
          '/api/pdfreport/generate',
          requestData,
          {
            headers: {
              'x-eqip-tenantid': tenantId,
              'Authorization': idToken,
            },
            responseType: 'blob'
          }
        );

        // Get the PDF blob
        const pdfBlob = response.data;
        
        // Create a URL for the blob and open in new tab
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const newTab = window.open(pdfUrl, '_blank');
        
        // Check if the tab was successfully opened
        if (newTab) {
          // Clean up the URL after a delay to prevent memory leaks
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
          
          // Hide loading after a short delay to ensure PDF is loading
          setTimeout(() => {
            setLoading(false);
            setIsGeneratingPdf(false);
          }, 1500);
          
          return { success: true, message: 'PDF opened in new tab' };
        } else {
          // If popup was blocked, clean up immediately and show error
          URL.revokeObjectURL(pdfUrl);
          setLoading(false);
          setIsGeneratingPdf(false);
          throw new Error('Popup blocked. Please allow popups for this site and try again.');
        }
        
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert(`Failed to generate PDF: ${error.message}`);
        setLoading(false);
        setIsGeneratingPdf(false);
      }
    }
  }));

  useEffect(
    () => {
      (async () => {
        try {
          setLoading(true);
          const requestHeader = {
            headers: {
              "x-eqip-tenantid": tenantId,
              Authorization: idToken,
            },
          };

          // Phase 1: core identification data — unblocks page render immediately
          const [responseAssignment, responseInspection] = await Promise.all([
            assignmentApi.get(`/assignments/inspection/${inspectionUid}`, requestHeader),
            inspectionApi.get(`/inspections/${inspectionUid}`),
          ]);

          const inspectionData = responseInspection.data.body[0];
          const assignmentData = responseAssignment?.data.body[0];
          const assignmentId = assignmentData?.id;
          const commodityId = assignmentData?.commodity_id;
          const bimoId = COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'BIMO')?.id;
          const lookupCategory = commodityId === bimoId ? LOOKUP_CATEGORIES.GENERAL_BIMO : LOOKUP_CATEGORIES.GENERAL;
          const lookupCodes = inspectionData.protocol_quality
            ?.map((item) => item.code)
            .filter((code) => code)
            .join("','");

          setInspection(inspectionData);
          setAssignment(assignmentData);
          setLoading(false);
          setDetailsLoading(true);
          setSecondaryLoading(true);

          // Phase 2A: fast secondary data (no images) — renders General, Exhibits, Signature sections
          Promise.all([
            codeTablePostgresApi.get(`/codetables/codetabletypes/6,7,10`),
            lookupApi.get(`/lookups/categories/${lookupCategory}`, requestHeader),
            ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspectionexhibitions`, requestHeader),
            lookupApi.get(`/lookups/lookupcodes/${lookupCodes}`),
            assignmentId
              ? assignmentAssignToApi.get(`/api/assignmentassigntos/assignmentid/${assignmentId}`, requestHeader)
              : Promise.resolve({ data: [] }),
            assignmentId
              ? assignmentApi.get(`/assignments/${assignmentId}/assignmentsignatures/typeid/${SIGNATURE_REPORT_TYPES.INSPECTION}`)
              : Promise.resolve({ data: { body: [] } }),
          ]).then(([rCodeTables, rLookupByGeneral, rExhibitions, rLookupsByCodes, rAssignTos, rSignatures]) => {
            const codeTablesData = rCodeTables.data.body;
            const codeTablesRole = codeTablesData.filter(item => item.code_table_type_id === 10);
            const assignmentAssignTos = rAssignTos?.data || [];
            const signaturesData = rSignatures?.data.body || [];

            const leadUserInfo = assignmentAssignTos.find(assignTo => {
              const role = codeTablesRole.find(r => r.id === assignTo.role_id);
              return role?.description?.toLowerCase().includes('lead') || role?.description?.toLowerCase().includes('inspector');
            });
            if (leadUserInfo) {
              const role = codeTablesRole.find(r => r.id === leadUserInfo.role_id);
              setLeadUser(`${leadUserInfo.assign_to} - ${role?.description || 'Inspector'}`);
            }

            const allowedAssignTosList = assignmentAssignTos.map((item) => {
              const foundRole = codeTablesRole.find(r => r.code_table_type_id === 10 && r.id === item.role_id);
              const foundSig = signaturesData.find(sig => sig.assign_to_email === item.assign_to);
              return {
                user_name: item.assign_to,
                roleUid: foundRole?.code_table_uid,
                roleName: foundRole?.description,
                signature_url: foundSig?.signature_url,
                assignment_signature_uid: foundSig?.assignment_signature_uid,
                allowToSign: item.assign_to === loggedInUser?.email,
              };
            });

            setCodeTables(codeTablesData);
            setLookupsByGeneral(rLookupByGeneral.data.body);
            setInspectionExhibitions(rExhibitions?.data);
            setLookups(rLookupsByCodes.data.body);
            setAssignmentSignatures(signaturesData);
            setAllowedAssignTos(allowedAssignTosList);
            setSecondaryLoading(false);
          }).catch(error => {
            console.error('Error loading secondary data:', error);
            setSecondaryLoading(false);
          });

          // Phase 2B: inspection details with images — slow (can be ~200 MB).
          // Uses a streaming fetch for inspectiondetails so we can track download
          // progress and show the user how much data has been received.
          const coreBaseUrl = process.env.REACT_APP_IEQIP_CORE_SERVICE_API_URL || '';
          const detailsUrl = `${coreBaseUrl}/api/inspections/${inspectionUid}/inspectiondetails`;
          const fetchDetailsWithProgress = async () => {
            const res = await fetch(detailsUrl, {
              headers: {
                'x-eqip-tenantid': tenantId,
                Authorization: idToken,
              },
            });
            const reader = res.body.getReader();
            const chunks = [];
            let loaded = 0;
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
              loaded += value.length;
              setDetailsLoadedBytes(loaded);
            }

            // Merge chunks in O(n) with a single pre-allocated buffer
            const merged = new Uint8Array(loaded);
            let offset = 0;
            for (const chunk of chunks) {
              merged.set(chunk, offset);
              offset += chunk.length;
            }

            // Parse the JSON in a Web Worker so the main thread stays responsive.
            // The ArrayBuffer is *transferred* (zero-copy) to the worker.
            setDetailsParsing(true);
            return new Promise((resolve, reject) => {
              const workerCode = `
                self.onmessage = function(e) {
                  try {
                    var text = new TextDecoder().decode(new Uint8Array(e.data));
                    var data = JSON.parse(text);
                    self.postMessage({ success: true, data: data });
                  } catch(err) {
                    self.postMessage({ success: false, error: err.message });
                  }
                };
              `;
              let worker;
              try {
                const blob = new Blob([workerCode], { type: 'application/javascript' });
                const workerUrl = URL.createObjectURL(blob);
                worker = new Worker(workerUrl);
                worker.onmessage = function(e) {
                  URL.revokeObjectURL(workerUrl);
                  worker.terminate();
                  setDetailsParsing(false);
                  if (e.data.success) resolve(e.data.data);
                  else reject(new Error(e.data.error));
                };
                worker.onerror = function(err) {
                  URL.revokeObjectURL(workerUrl);
                  worker.terminate();
                  setDetailsParsing(false);
                  reject(err);
                };
                // Transfer the buffer — zero-copy, no main-thread blocking
                worker.postMessage(merged.buffer, [merged.buffer]);
              } catch (workerErr) {
                // Fallback: parse synchronously if Workers are unavailable (e.g. CSP)
                if (worker) worker.terminate();
                try {
                  const text = new TextDecoder().decode(merged);
                  setDetailsParsing(false);
                  resolve(JSON.parse(text));
                } catch (parseErr) {
                  setDetailsParsing(false);
                  reject(parseErr);
                }
              }
            });
          };

          Promise.allSettled([
            fetchDetailsWithProgress(),
            ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`),
            ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503aoperations`),
          ]).then(([rDetails, r503b, r503a]) => {
            if (rDetails.status === 'fulfilled') setInspectionDetails(rDetails.value);
            setB503Operations(r503b.status === 'fulfilled' ? (r503b.value?.data?.body || r503b.value?.data || []) : []);
            setA503Operations(r503a.status === 'fulfilled' ? (r503a.value?.data?.body || r503a.value?.data || []) : []);
            setDetailsLoading(false);
          });

        } catch (error) {
          console.error(error);
          setLoading(false);
          setDetailsLoading(false);
          setSecondaryLoading(false);
        }
      })();
    },
    // eslint-disable-next-line
    []
  );

  // Generate TOC with realistic page number estimates based on typical content patterns
  const generateTOCSections = () => {
    const sections = [];
    
    // Start with GENERAL section on page 1
    sections.push({ 
      label: "GENERAL", 
      id: "summary-section",
      page: 1
    });

    // Add GeneralReport group headers dynamically
    const generalGroups = lookupsByGeneral?.filter((lookupByGeneral) => lookupByGeneral.parent_uid === null) || [];
    
    // Filter protocol qualities by "SM" codes to match GeneralReport logic
    const foundProtocolQualities = inspection?.protocol_quality?.filter((protocol_quality) => {
      return protocol_quality?.code?.startsWith("SM");
    }) || [];

    // Based on the image, the dynamic sections appear to be on pages 2-5
    let pageCounter = 2;

    // Add each dynamic group that has content
    generalGroups.forEach((group) => {
      const foundProtocolQualitiesByGroup = foundProtocolQualities?.filter((foundProtocolQuality) => 
        lookups?.find(lookup => lookup.code === foundProtocolQuality.code)?.parent_uid === group.lookup_uid
      );
      
      // Only add to TOC if the group has associated protocol qualities
      if (foundProtocolQualitiesByGroup && foundProtocolQualitiesByGroup.length > 0) {
        const groupId = `group-${group.lookup_uid}`;
        sections.push({
          label: group.description.toUpperCase(),
          id: groupId,
          page: pageCounter
        });
        pageCounter += 1;
      }
    });

    // Add remaining static sections - starting where dynamic sections end
    const staticSections = [
      { label: "SYSTEM COVERAGE", id: "system-coverage-section" },
      { label: "OBSERVATION/MAJOR FINDING", id: "observation-section" },
      { label: "DISCUSSION/MINOR FINDING", id: "discussion-section" },
      { label: "EXHIBITS", id: "exhibitions-section" },
      { label: "SIGNATURE", id: "signature-section" }
    ];

    staticSections.forEach(section => {
      sections.push({
        label: section.label,
        id: section.id,
        page: pageCounter
      });
      pageCounter += 1;
    });

    return sections;
  };

  const dynamicTOCSections = generateTOCSections();

  return (
    <Box ref={ref} className="print-root" sx={{ background: '#fff', color: '#222', fontFamily: 'serif', padding: 4, minHeight: '100vh', position: 'relative' }}>
      <PrintWatermark text={watermarkText} />
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ color: '#fff', textAlign: 'center' }}>
            {isGeneratingPdf ? 'Generating PDF Report...' : 'Loading inspection data...'}
          </Typography>
          {isGeneratingPdf && (
            <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', opacity: 0.8 }}>
              This may take a few moments. The PDF will open in a new tab when ready.
            </Typography>
          )}
        </Box>
      </Backdrop>
      {/* Sticky progress banner — stays visible until ALL data is fully loaded */}
      {(secondaryLoading || detailsLoading) && (
        <Box
          className="screen-only"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            backgroundColor: detailsParsing ? '#2e7d32' : '#1565c0',
            color: '#fff',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            transition: 'background-color 0.3s',
          }}
        >
          <CircularProgress size={20} sx={{ color: '#fff', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {detailsParsing ? (
                <>
                  Processing report data&hellip;
                  <span style={{ fontWeight: 400, marginLeft: 8 }}>
                    {(detailsLoadedBytes / 1048576).toFixed(1)} MB — building report, please wait
                  </span>
                </>
              ) : detailsLoading ? (
                <>
                  Downloading inspection report data&hellip;
                  {detailsLoadedBytes > 0 && (
                    <span style={{ fontWeight: 400, marginLeft: 8 }}>
                      {(detailsLoadedBytes / 1048576).toFixed(1)} MB received — this report is large, please wait
                    </span>
                  )}
                </>
              ) : (
                <>
                  Loading report data&hellip;
                  <span style={{ fontWeight: 400, marginLeft: 8 }}>
                    finalising sections, almost done
                  </span>
                </>
              )}
            </Typography>
            <LinearProgress sx={{ mt: 0.75, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: '#fff' } }} />
          </Box>
        </Box>
      )}

      {/* Header - show on screen */}
      <Box className="screen-only">
        {/* Centered Title */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.3rem', marginBottom: 1, textAlign: 'center' }}>
          Establishment Inspection Report
        </Typography>
        
        <ReportHeader className="report-header">
          <HeaderLeft>
            <Typography><b>Auditing Firm:</b> {assignment?.auditing_firm_name}</Typography>
            {/* <Typography><b>Address:</b> {assignment?.auditing_firm_address}</Typography> */}
          </HeaderLeft>
          <HeaderLeft>
            <Typography><b>Audited Firm:</b> {assignment?.audited_firm_name}</Typography>
            <Typography><b>Address:</b> {assignment?.audited_firm_address}</Typography>
          </HeaderLeft>
          <HeaderRight>
            <Typography><b>Start:</b> {assignment?.inspection_dates?.length ? getGlobalDate(Math.min(...assignment.inspection_dates.map(date => parseDateWithoutTimezone(date)))) : "-"}</Typography>
            <Typography><b>End:</b> {assignment?.inspection_dates?.length ? getGlobalDate(Math.max(...assignment.inspection_dates.map(date => parseDateWithoutTimezone(date)))) : "-"}</Typography>
          </HeaderRight>
        </ReportHeader>
      </Box>

      {/* Print Header for first page */}
      <PrintHeader assignment={assignment} />

      {/* Table of Contents */}
      <Box sx={{ marginBottom: 4 }} className="table-of-contents">
        <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>TABLE OF CONTENTS</Typography>
        <Typography variant="caption" sx={{ fontStyle: 'italic', marginBottom: 2, display: 'block', color: '#666' }}>
          Note: Page numbers cannot be accurately predicted due to dynamic content. Please refer to section headers in the printed document.
        </Typography>
        {dynamicTOCSections.map((section) => (
          <TOCRow key={section.label}>
            <a href={`#${section.id}`} style={{ color: '#222', textDecoration: 'none', flex: 1 }}>{section.label}</a>
          </TOCRow>
        ))}
      </Box>

      {/* Sections */}
      <SectionWrapper id="summary-section" sx={{ scrollMarginTop: '100px' }}>
        <SectionTitle>GENERAL</SectionTitle>
        <div className="section-content">
          {secondaryLoading
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading inspection data&hellip;
                  {detailsLoadedBytes > 0 && ` (${(detailsLoadedBytes / 1048576).toFixed(1)} MB received)`}
                </Typography>
              </Box>
            : <GeneralReport inspection={inspection} inspectionDetails={inspectionDetails} lookupsByGeneral={lookupsByGeneral} lookups={lookups} assignment={assignment} leadUser={leadUser} allowedAssignTos={allowedAssignTos} detailsLoading={detailsLoading} />
          }
        </div>
      </SectionWrapper>
      
      <SectionWrapper id="system-coverage-section" sx={{ scrollMarginTop: '100px' }}>
        <PrintHeader assignment={assignment} />
        <SectionTitle>SYSTEM COVERAGE</SectionTitle>
        <div className="section-content">
          {detailsLoading
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading inspection data&hellip;
                  {detailsLoadedBytes > 0 && ` (${(detailsLoadedBytes / 1048576).toFixed(1)} MB received)`}
                </Typography>
              </Box>
            : <CFRReport
                inspection={inspection}
                inspectionDetails={inspectionDetails}
                codeTables={codeTables}
                lookups={lookups}
                b503Operations={b503Operations}
                a503Operations={a503Operations}
              />
          }
        </div>
      </SectionWrapper>

      <SectionWrapper id="observation-section" sx={{ scrollMarginTop: '100px' }}>
        <PrintHeader assignment={assignment} />
        <SectionTitle>OBSERVATION/MAJOR FINDING</SectionTitle>
        <div className="section-content">
          {detailsLoading
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading inspection data&hellip;
                  {detailsLoadedBytes > 0 && ` (${(detailsLoadedBytes / 1048576).toFixed(1)} MB received)`}
                </Typography>
              </Box>
            : <CitationReport
                inspection={inspection}
                inspectionDetails={inspectionDetails}
                b503Operations={b503Operations}
                a503Operations={a503Operations}
              />
          }
        </div>
      </SectionWrapper>

      <SectionWrapper id="discussion-section" sx={{ scrollMarginTop: '100px' }}>
        <PrintHeader assignment={assignment} />
        <SectionTitle>DISCUSSION/MINOR FINDING</SectionTitle>
        <div className="section-content">
          {detailsLoading
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading inspection data&hellip;
                  {detailsLoadedBytes > 0 && ` (${(detailsLoadedBytes / 1048576).toFixed(1)} MB received)`}
                </Typography>
              </Box>
            : <DiscussionReport
                inspection={inspection}
                inspectionDetails={inspectionDetails}
                b503Operations={b503Operations}
                a503Operations={a503Operations}
              />
          }
        </div>
      </SectionWrapper>
      
      <SectionWrapper id="exhibitions-section" sx={{ scrollMarginTop: '100px' }}>
        <PrintHeader assignment={assignment} />
        <SectionTitle>EXHIBITS</SectionTitle>
        <div className="section-content">
          <ExhibitionReport inspectionUid={inspectionUid} inspectionExhibitions={inspectionExhibitions} />
        </div>
      </SectionWrapper>
      
      <SectionWrapper id="signature-section" sx={{ scrollMarginTop: '100px' }}>
        <PrintHeader assignment={assignment} />
        <SectionTitle>SIGNATURE</SectionTitle>
        <div className="section-content">
          {detailsLoading
            ? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading inspection data&hellip;
                  {detailsLoadedBytes > 0 && ` (${(detailsLoadedBytes / 1048576).toFixed(1)} MB received)`}
                </Typography>
              </Box>
            : <EmployeeSignature
                inspectionUid={inspectionUid}
                inspectionDetails={inspectionDetails}
                assignment={assignment}
                assignmentSignatures={assignmentSignatures}
                allowedAssignTos={allowedAssignTos}
                codeTables={codeTables}
              />
          }
        </div>
      </SectionWrapper>

      {/* Print styles for page breaks and pagination */}
      <style type="text/css" media="print">{`
        @page {
          margin: 15px 15px 40px 15px;
          size: A4;
          @bottom-left {
            content: "";
          }
          @bottom-center {
            content: counter(page) "/" counter(pages);
            font-size: 10px;
            color: #666;
          }
          @bottom-right {
            content: "";
          }
          @top-left {
            content: "";
          }
          @top-center {
            content: "";
          }
          @top-right {
            content: "";
          }
        }

        @media print {
          body * {
            visibility: hidden !important;
          }
          .print-root, .print-root * {
            visibility: visible !important;
          }
          .print-root {
            position: absolute !important;
            left: 0;
            top: 0;
            width: 100vw !important;
            background: #fff !important;
            z-index: 9999 !important;
            margin: 0 !important;
            padding: 15px !important;
            box-sizing: border-box !important;
          }
          
          /* Hide screen header during print */
          .screen-only {
            display: none !important;
          }
          
          /* Show print headers only during print */
          .print-header-only {
            display: block !important;
          }

          /* Compact print header for first page */
          .print-header-only {
            margin-bottom: 12px !important;
          }

          .print-header-only .MuiBox-root {
            margin-bottom: 8px !important;
            padding-bottom: 6px !important;
          }

          .print-header-only .MuiTypography-root {
            margin-bottom: 2px !important;
            line-height: 1.2 !important;
          }

          .print-header-only .MuiTypography-h6 {
            font-size: 1rem !important;
            margin-bottom: 4px !important;
          }

          /* Table of Contents - Keep on first page and compact */
          .table-of-contents {
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 16px !important;
            background: #fff !important;
            border: none !important;
            box-shadow: none !important;
          }

          /* Reduce TOC spacing to fit better on first page */
          .table-of-contents .MuiBox-root {
            margin-bottom: 1px !important;
          }

          .table-of-contents .MuiTypography-h6 {
            margin-bottom: 6px !important;
            font-size: 1.1rem !important;
          }

          /* Make TOC rows more compact for print */
          .table-of-contents div[class*="TOCRow"] {
            margin-bottom: 1px !important;
            font-size: 0.85rem !important;
            line-height: 1.1 !important;
            padding: 1px 0 !important;
          }

          /* Professional section styling for print */
          .MuiBox-root[id*="section"] {
            background: #fff !important;
            border: 1px solid #ddd !important;
            box-shadow: none !important;
            margin-bottom: 16px !important;
          }

          /* Section titles for print */
          .MuiTypography-root[class*="SectionTitle"] {
            background: #f8f9fa !important;
            color: #333 !important;
            margin-top: 0 !important;
            margin-bottom: 12px !important;
            padding: 8px 16px !important;
            font-size: 1.1rem !important;
            border-left: 4px solid #1976d2 !important;
            box-shadow: none !important;
          }

          /* Section content padding for print */
          .section-content {
            padding: 12px 16px !important;
          }

          /* Force GENERAL section to start on first page after TOC */
          #summary-section {
            page-break-before: avoid !important;
            page-break-after: auto !important;
          }
          
          /* Ensure each section starts on a new page except first */
          #system-coverage-section,
          #observation-section,
          #discussion-section,
          #exhibitions-section,
          #signature-section {
            page-break-before: always !important;
            padding-top: 0 !important;
          }

          /* Remove any gradient backgrounds for print */
          *::after,
          *::before {
            background: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </Box>
  );
});

export default InspectionReport;
